<script lang="ts">
  import { 
    X, CreditCard, Building2, ShieldCheck, CheckCircle2, 
    Lock, ArrowRight, Download, ExternalLink, AlertTriangle,
    Sparkles, RefreshCw
  } from 'lucide-svelte';

  interface Props {
    open: boolean;
    invoiceId: string;
    propertyId?: number;
    amount?: number;
    unitLabel?: string;
    tenantName?: string;
    invoiceNumber?: string;
    onPaid?: () => void;
  }

  let {
    open = $bindable(false),
    invoiceId,
    propertyId = 700,
    amount = 42.50,
    unitLabel = '201',
    tenantName = 'Resident',
    invoiceNumber = 'INV-202609-01',
    onPaid = () => {}
  }: Props = $props();

  let paymentMethod = $state<'card' | 'ach'>('card');
  let processing = $state(false);
  let step = $state<'form' | 'authorizing' | 'success'>('form');
  let errorMessage = $state('');

  // Form Fields
  let cardNumber = $state('4242 •••• •••• 4242');
  let cardExpiry = $state('12/28');
  let cardCvc = $state('123');
  let cardZip = $state('90210');
  let cardHolder = $state('');

  $effect(() => {
    if (tenantName && !cardHolder) {
      cardHolder = tenantName;
    }
  });

  // ACH Fields
  let selectedBank = $state('chase');
  let achRouting = $state('122000496');
  let achAccount = $state('••••••••8819');
  let achAccountType = $state<'checking' | 'savings'>('checking');

  // Intent Result
  let intentId = $state('');
  let receiptUrl = $state('');

  async function handleStripeCheckout() {
    processing = true;
    errorMessage = '';
    step = 'authorizing';

    try {
      // 1. Obtain Payment Intent from API
      const res = await fetch(`/api/invoices/${invoiceId}/stripe-intent?property_id=${propertyId}`, {
        method: 'POST'
      });
      if (!res.ok) throw new Error('Failed to create payment intent');
      const data = await res.json();
      intentId = data.intent_id || `pi_${Math.random().toString(36).substring(2, 10)}`;

      // 2. Simulate 3D Secure / Bank Settlement
      await new Promise(r => setTimeout(r, 300));

      // 3. Dispatch Webhook to backend to finalize payment reconciliation
      const webhookPayload = {
        type: 'payment_intent.succeeded',
        data: {
          object: {
            id: intentId,
            amount: Math.round(amount * 100),
            status: 'succeeded',
            receipt_url: `https://pay.stripe.com/receipts/${intentId}`,
            invoice_id: invoiceId
          }
        }
      };

      await fetch('/api/webhooks/stripe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(webhookPayload)
      });

      receiptUrl = `https://pay.stripe.com/receipts/${intentId}`;
      step = 'success';
      onPaid();
    } catch (e: any) {
      errorMessage = e.message || 'Payment authorization failed';
      step = 'form';
    } finally {
      processing = false;
    }
  }

  function handleClose() {
    open = false;
    setTimeout(() => {
      step = 'form';
      errorMessage = '';
    }, 300);
  }
</script>

{#if open}
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in font-sans">
    <div class="bg-base-100 border border-base-200 rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden flex flex-col">
      
      <!-- Modal Header -->
      <div class="px-6 py-4 border-b border-base-200 bg-base-200/50 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-2xl bg-indigo-500/10 text-indigo-600 flex items-center justify-center font-bold shadow-xs">
            <Lock class="w-5 h-5" />
          </div>
          <div>
            <h3 class="font-extrabold text-base-content text-base leading-tight flex items-center gap-2">
              Stripe Secure Checkout
              <span class="badge badge-xs badge-primary font-bold uppercase">256-Bit SSL</span>
            </h3>
            <p class="text-xs text-base-content/60">Utility Remittance for Suite {unitLabel}</p>
          </div>
        </div>

        <button 
          class="btn btn-sm btn-ghost btn-circle text-base-content/50 hover:text-base-content"
          onclick={handleClose}
          disabled={processing}
        >
          <X class="w-4 h-4" />
        </button>
      </div>

      <!-- Modal Body -->
      <div class="p-6 space-y-5">
        
        {#if errorMessage}
          <div class="p-3 rounded-2xl bg-error/10 border border-error/20 text-error text-xs font-bold flex items-center gap-2">
            <AlertTriangle class="w-4 h-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        {/if}

        {#if step === 'form'}
          <!-- Invoice Amount Banner -->
          <div class="p-4 rounded-2xl bg-gradient-to-r from-primary/10 via-base-200 to-transparent border border-base-200 flex items-center justify-between">
            <div>
              <span class="text-[11px] font-bold text-base-content/50 uppercase tracking-wider block">Total Amount Due</span>
              <span class="text-2xl font-black text-base-content font-mono tracking-tight">${amount.toFixed(2)}</span>
            </div>
            <div class="text-right text-xs">
              <span class="text-base-content/60 block">Statement: <strong>{invoiceNumber}</strong></span>
              <span class="text-success font-bold flex items-center gap-1 justify-end mt-0.5">
                <ShieldCheck class="w-3.5 h-3.5" /> Instant Receipt
              </span>
            </div>
          </div>

          <!-- Method Picker -->
          <div class="grid grid-cols-2 gap-2">
            <button 
              type="button"
              onclick={() => paymentMethod = 'card'}
              class="p-3 rounded-2xl border text-left flex items-center gap-3 transition-all {paymentMethod === 'card' ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-base-200 hover:border-base-300'}"
            >
              <CreditCard class="w-5 h-5 {paymentMethod === 'card' ? 'text-primary' : 'text-base-content/60'}" />
              <div>
                <div class="text-xs font-bold text-base-content">Credit / Debit Card</div>
                <div class="text-[10px] text-base-content/50">Visa, MC, Amex</div>
              </div>
            </button>

            <button 
              type="button"
              onclick={() => paymentMethod = 'ach'}
              class="p-3 rounded-2xl border text-left flex items-center gap-3 transition-all {paymentMethod === 'ach' ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-base-200 hover:border-base-300'}"
            >
              <Building2 class="w-5 h-5 {paymentMethod === 'ach' ? 'text-primary' : 'text-base-content/60'}" />
              <div>
                <div class="text-xs font-bold text-base-content">Plaid ACH Transfer</div>
                <div class="text-[10px] text-base-content/50">Direct Bank Debit</div>
              </div>
            </button>
          </div>

          <!-- Card Inputs -->
          {#if paymentMethod === 'card'}
            <div class="space-y-3 p-4 rounded-2xl bg-base-200/50 border border-base-200 text-xs">
              <div>
                <label class="label py-0.5 font-bold text-base-content/70">Cardholder Name</label>
                <input type="text" bind:value={cardHolder} class="input input-sm input-bordered rounded-xl w-full" />
              </div>
              <div>
                <label class="label py-0.5 font-bold text-base-content/70">Card Number</label>
                <div class="relative">
                  <input type="text" bind:value={cardNumber} class="input input-sm input-bordered rounded-xl w-full font-mono font-bold" />
                  <span class="badge badge-sm badge-neutral absolute right-2.5 top-2 font-mono text-[10px]">VISA</span>
                </div>
              </div>
              <div class="grid grid-cols-3 gap-2">
                <div>
                  <label class="label py-0.5 font-bold text-base-content/70">Expires</label>
                  <input type="text" bind:value={cardExpiry} class="input input-sm input-bordered rounded-xl w-full font-mono" placeholder="MM/YY" />
                </div>
                <div>
                  <label class="label py-0.5 font-bold text-base-content/70">CVC</label>
                  <input type="password" bind:value={cardCvc} class="input input-sm input-bordered rounded-xl w-full font-mono" placeholder="•••" />
                </div>
                <div>
                  <label class="label py-0.5 font-bold text-base-content/70">Postal Code</label>
                  <input type="text" bind:value={cardZip} class="input input-sm input-bordered rounded-xl w-full font-mono" />
                </div>
              </div>
            </div>
          {:else}
            <!-- ACH Inputs -->
            <div class="space-y-3 p-4 rounded-2xl bg-base-200/50 border border-base-200 text-xs">
              <div>
                <label class="label py-0.5 font-bold text-base-content/70">Select Financial Institution</label>
                <select bind:value={selectedBank} class="select select-sm select-bordered rounded-xl w-full">
                  <option value="chase">JPMorgan Chase Bank</option>
                  <option value="bofa">Bank of America</option>
                  <option value="wells">Wells Fargo</option>
                  <option value="citi">Citibank</option>
                </select>
              </div>
              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label class="label py-0.5 font-bold text-base-content/70">Routing (ABA)</label>
                  <input type="text" bind:value={achRouting} class="input input-sm input-bordered rounded-xl w-full font-mono" />
                </div>
                <div>
                  <label class="label py-0.5 font-bold text-base-content/70">Account Number</label>
                  <input type="text" bind:value={achAccount} class="input input-sm input-bordered rounded-xl w-full font-mono" />
                </div>
              </div>
              <div class="p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-800 text-[11px] flex items-center gap-2">
                <CheckCircle2 class="w-4 h-4 text-emerald-600 shrink-0" />
                <span>Instant micro-deposit verification active via Plaid Auth.</span>
              </div>
            </div>
          {/if}

          <!-- Pay Button -->
          <button 
            type="button"
            onclick={handleStripeCheckout}
            class="btn btn-primary w-full rounded-2xl font-bold text-white shadow-lg gap-2"
          >
            <Lock class="w-4 h-4" />
            <span>Pay ${amount.toFixed(2)} via Stripe</span>
            <ArrowRight class="w-4 h-4" />
          </button>

        {:else if step === 'authorizing'}
          <!-- Authorizing Spinner State -->
          <div class="py-12 flex flex-col items-center justify-center space-y-4 text-center">
            <span class="loading loading-spinner loading-lg text-primary"></span>
            <div>
              <h4 class="font-bold text-base text-base-content">Authorizing Payment...</h4>
              <p class="text-xs text-base-content/60 mt-1">Connecting with Stripe 3D-Secure bank gateway</p>
            </div>
          </div>

        {:else if step === 'success'}
          <!-- Success State -->
          <div class="py-6 flex flex-col items-center justify-center space-y-4 text-center animate-fade-in">
            <div class="w-16 h-16 rounded-3xl bg-success/15 text-success flex items-center justify-center shadow-md">
              <CheckCircle2 class="w-10 h-10" />
            </div>
            <div>
              <h4 class="font-black text-xl text-base-content">Payment Successful!</h4>
              <p class="text-xs text-base-content/60 mt-1">
                Your payment of <strong>${amount.toFixed(2)}</strong> has been processed and credited to your ledger.
              </p>
            </div>

            <div class="p-4 rounded-2xl bg-base-200/80 border border-base-200 text-xs w-full text-left space-y-1.5 font-mono">
              <div class="flex justify-between text-base-content/70">
                <span>Stripe Intent ID:</span>
                <strong class="text-primary truncate max-w-[180px]">{intentId}</strong>
              </div>
              <div class="flex justify-between text-base-content/70">
                <span>Payment Reference:</span>
                <strong class="text-base-content font-bold">ACH-STRIPE-SETTLED</strong>
              </div>
              <div class="flex justify-between text-base-content/70">
                <span>Timestamp:</span>
                <span>{new Date().toLocaleTimeString()}</span>
              </div>
            </div>

            <div class="flex gap-2 w-full pt-2">
              <button 
                type="button" 
                onclick={handleClose} 
                class="btn btn-sm btn-primary flex-1 rounded-xl font-bold text-white"
              >
                Done
              </button>
              <a 
                href={receiptUrl} 
                target="_blank" 
                class="btn btn-sm btn-outline btn-ghost rounded-xl gap-1"
              >
                <ExternalLink class="w-3.5 h-3.5" />
                Receipt
              </a>
            </div>
          </div>
        {/if}

      </div>
    </div>
  </div>
{/if}
